<template>
    <div class="video_call_notification_popup">
        <div class="video_call_notification_popup_center">
            <div class="video_call_notification_popup_center_content">
                <div class="video_call_notification_popup_inner">
                    <div class="video_call_notification_popup_header">
                        <h3>Incoming Call</h3>

                        <span
                            class="close_icon"
                            role="button"
                            aria-label="Decline call"
                            @click.stop="callDeclinedMethod"
                        >
                            <i class="fa fa-times-circle" aria-hidden="true"></i>
                        </span>
                    </div>

                    <div class="video_call_notification_popup_body">
                        <h5>{{ callerInfo.callerName }} is calling you.</h5>
                        <p>The call will start as soon as you answer.</p>
                    </div>

                    <div class="video_call_notification_popup_footer">
                        <button
                            type="button"
                            class="btn btn-danger mr-3"
                            :disabled="isProcessing"
                            @click="callDeclinedMethod"
                        >
                            Decline
                        </button>

                        <button
                            type="button"
                            class="btn btn-primary"
                            :disabled="isProcessing"
                            @click="callAcceptMethod"
                        >
                            <i class="fa fa-phone mr-1" aria-hidden="true"></i>
                            Accept
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { bus } from '@/main';
import callingRingtone from '@/assets/audio/videocall_ringtone.mp3';
import commonSocket from '@/commonSocket/index.js';

const CALL_TIMEOUT = 30000;

export default {
    name: 'video-call-notification-popup',

    props: {
        callerInfo: {
            type: Object,
            required: true
        },

        roomId: {
            type: [String, Number],
            required: true
        }
    },

    data() {
        return {
            audioPlayer: null,
            autoCallDisconnectTimeout: null,
            isProcessing: false
        };
    },

    mounted() {
        this.startCallNotification();
    },

    beforeDestroy() {
        this.cleanup();
    },

    methods: {
        startCallNotification() {
            this.playSound();

            this.autoCallDisconnectTimeout = setTimeout(() => {
                this.callNotAnsweredFun();
            }, CALL_TIMEOUT);
        },

        playSound() {
            this.stopSound();

            this.audioPlayer = new Audio(callingRingtone);
            this.audioPlayer.loop = true;

            const playPromise = this.audioPlayer.play();

            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(error => {
                    // Browser may block autoplay until user interaction.
                    console.warn(
                        'Unable to autoplay call ringtone:',
                        error
                    );
                });
            }
        },

        stopSound() {
            if (!this.audioPlayer) {
                return;
            }

            this.audioPlayer.pause();
            this.audioPlayer.currentTime = 0;
            this.audioPlayer.src = '';
            this.audioPlayer.load();

            this.audioPlayer = null;
        },

        clearCallTimeout() {
            if (this.autoCallDisconnectTimeout) {
                clearTimeout(this.autoCallDisconnectTimeout);
                this.autoCallDisconnectTimeout = null;
            }
        },

        cleanup() {
            this.stopSound();
            this.clearCallTimeout();

            document.body.classList.remove('videoNotificationPopup');
        },

        closeNotification() {
            bus.$emit('callerNotifiactionBus', false);
        },

        callAcceptMethod() {
            if (this.isProcessing) {
                return;
            }

            this.isProcessing = true;

            this.stopSound();
            this.clearCallTimeout();

            commonSocket.emit({
                type: 'videoCallAccepted',
                targetId: this.callerInfo.callerID,
                roomId: this.roomId
            });

            this.closeNotification();

            this.$router.push(`/video/conference/#${this.roomId}`);
        },

        callDeclinedMethod() {
            if (this.isProcessing) {
                return;
            }

            this.isProcessing = true;

            this.stopSound();
            this.clearCallTimeout();

            commonSocket.emit({
                type: 'videoCallDecline',
                targetId: this.callerInfo.callerID,
                callerID: this.callerInfo.targetId
            });

            this.closeNotification();
        },

        callNotAnsweredFun() {
            if (this.isProcessing) {
                return;
            }

            this.isProcessing = true;

            this.stopSound();
            this.clearCallTimeout();

            commonSocket.emit({
                type: 'videoCallNotReceived',
                message: 'Call Not received.',
                chatRoomId: this.roomId,
                targetId: this.callerInfo.callerID,
                callerID: this.callerInfo.targetId
            });

            this.closeNotification();
        }
    }
};
</script>

<style lang="scss" scoped>
.video_call_notification_popup {
    position: fixed;
    inset: 0;
    z-index: 100000;
    background: rgba(0, 0, 0, 0.9);
    font-family:
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        "Helvetica Neue",
        Arial,
        sans-serif;
    color: #fff;
}

.video_call_notification_popup_center {
    display: table;
    width: 100%;
    height: 100%;
}

.video_call_notification_popup_center_content {
    display: table-cell;
    vertical-align: middle;
    padding: 20px;
}

.video_call_notification_popup_inner {
    position: relative;
    width: 100%;
    max-width: 540px;
    margin: 0 auto;
    padding: 20px;
    background: #1c1e1f;
    border-radius: 5px;
}

.video_call_notification_popup_header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
        margin: 0;
    }
}

.video_call_notification_popup_body {
    margin: 30px 0;

    h5 {
        margin-bottom: 10px;
    }

    p {
        margin-bottom: 0;
    }
}

.close_icon {
    font-size: 26px;
    line-height: 1;
    cursor: pointer;

    &:focus {
        outline: none;
    }

    .fa {
        cursor: pointer;
    }
}

.video_call_notification_popup_footer {
    display: flex;
    justify-content: flex-end;
}
</style>